// ============================================================
// BattlefieldPanel's props: what an absent one means
// ============================================================
// The panel takes some ninety props, and most of them are optional: it is
// rendered bare in its own tests, in the map Workshop with no roster, and
// in a session. This table is the single statement of what "absent"
// means for each of them - the panel's signature used to carry it as a
// destructuring default, which made the component itself 150 lines long
// before its first statement.

import { makeBoundary } from '../../engine/battlefield/boundary';

/** Shared empties: identity keeps the memos fed from them from re-running. */
export const EMPTY = [];
export const EMPTY_REACH = {};
/** An empty record by figure id, for a layer the classic map does not draw. */
export const EMPTY_RECORD = Object.freeze({});
const EMPTY_TOKENS = {};
const EMPTY_SURFACES = {};
export const EMPTY_OVERLAPS = {};
export const EMPTY_STATUS = {};

/**
 * A module constant, not a fresh makeBoundary() per render: the camera
 * re-fits on the boundary's identity, and a new object every render would
 * re-frame the map continuously.
 */
export const DEFAULT_BOUNDARY = makeBoundary();

/**
 * Defaults to LOCKED. An absent permission must not open a GM tool - the
 * shell's own `isLocked` reads DEFAULT_LOCKS, where `deployment` is locked
 * until granted, and the fallback here has to agree with it.
 */
const ALWAYS_LOCKED = () => true;

/**
 * The value each optional prop takes when it is absent. Declarative: one
 * row per prop, which is why it runs long. The notes are the props' own
 * documentation - see BattlefieldPanel's header for the ones with none.
 */
const PANEL_DEFAULTS = {
  tokens: EMPTY_TOKENS,
  roster: EMPTY,
  actorId: null,
  targetId: null,
  isGm: false,
  isLocked: ALWAYS_LOCKED,
  // Pings - speech aimed at the map. Absent handlers simply mean a panel
  // with no session behind it, which is how its own tests render it.
  pings: EMPTY,
  // `{ x, z, n }` - point the camera here. See `lookAtOnMap`.
  focusOn: null,
  // House rules: does going up cost, and does training make it cheaper?
  climbCosts: false,
  climbTraining: true,
  levelPickerSpends: true,
  // The GM's staged combatants - see `useWaitingRooms`. Empty for anyone without the grant.
  waitingRooms: EMPTY,
  activeRoomId: null,
  // The wings - `useScenes`' return - and the stage document, for Scenes
  // mode. Null for a panel with no session behind it, and unused by a
  // player, who never has the mode (v8.338.0).
  scenes: null,
  stage: null,
  // `useStage`'s card: the title card to show now, or null.
  stageCard: null,
  canApplyDerived: false,
  liveMeasurement: null,
  coverPinned: false,
  ignoreTerrain: false,
  library: EMPTY,
  // False in the Workshop, which has no session library - see BattlefieldMapAdmin.
  sessionLibrary: true,
  // The renderer this panel draws with: the session's is the display (v12.0.0); the Assets editors pass 'classic'.
  renderer: 'display',
  deployment: null,
  redeployOnMapLoad: false,
  // The live magazine of the weapon the actor has in hand, or null.
  //
  // PER-VIEWER ON PURPOSE, and it will be reported as a sync bug if this
  // is not said out loud: `selectedSlot` is local to the attack panel,
  // exactly as `actorId` is, so two clients acting as the same combatant
  // legitimately show different ticks on the same marker.
  actorAmmo: null,
  // The table's vehicle arc preset (engine/battlefield/facing): the display's facing ring draws its arcs.
  vehicleArcPreset: 'quarters',
  blastRadius: 0,
  // TOTAL included angle of a Spray cone, in degrees. Zero for other weapons.
  sprayDegrees: 0,
  beamActive: false,
  // How far the cone reaches - the weapon's own range, in metres.
  weaponRangeM: 0,
  // The selected weapon's template at its EXPECTATION (v8.372.5) - a
  // ResolvedArea from `expectedArea`. When given it decides the shape drawn;
  // the four props above are the older callers' way and still work.
  area: null,
  // The dice behind the template's sizes, for the overlay label.
  areaSizeHint: '',
  // Where the tape currently ends, reported upward for the attack to read.
  //
  // A REF, not a callback prop: the line changes on every mousemove of a
  // drag, and lifting it into the application's state would re-render the
  // whole tree at sixty hertz. Nobody renders from this - it is read once,
  // when somebody fires.
  measureLineRef: null,
  // The combatant under the pointer, here or on the order rail (v8.319.0).
  hoveredId: null,
  // The actor's reach to each token, and the chip on the firing line (v8.323.0).
  reachMarks: EMPTY_REACH,
  firingLine: null,
  // What just happened to whom, for the marker to wear a moment (v8.326.0).
  impacts: EMPTY_REACH,
  // Who moved lately, by actor, for the display to walk (plan design-gaps, M3).
  walks: EMPTY_REACH,
  applyConditionModifiers: true,
  fatigueSlowsMovement: false,
  surfaceEffects: EMPTY_SURFACES,
};

/**
 * The props with every absent optional one filled in from PANEL_DEFAULTS.
 *
 * "Absent" means `undefined`, exactly as a destructuring default reads it:
 * an explicit `null` is a value and is kept.
 */
export function withPanelDefaults(props) {
  const out = { ...props };
  for (const [key, value] of Object.entries(PANEL_DEFAULTS)) {
    if (out[key] === undefined) out[key] = value;
  }
  return out;
}
